import * as Sentry from "@sentry/react";
import * as React from "react";
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from "react-router-dom";
import type { Error as AppError } from "@/util-components/root/types";
import frontendPackageJson from "../../../package.json";

const DEFAULT_API_URL = "http://localhost:3001/v1.0";
const FRONTEND_APP_NAME = frontendPackageJson.name;
const FRONTEND_APP_VERSION = frontendPackageJson.version;
const FRONTEND_RELEASE = process.env.REACT_APP_SENTRY_RELEASE || `${FRONTEND_APP_NAME}@${FRONTEND_APP_VERSION}`;

const IGNORED_ERRORS = [
  "NoUserFoundForbidden",
  "NoOrganizationForbidden",
  "EMailNotVerifiedForbidden",
  "NoBillingContractForbidden",
  "IncompleteProfileForbidden",
  "BannedByAdminForbidden",
  "DeactivatedUserForbidden",
  "DeactivatedByAdminForbidden",
  "InactiveSubscriptionForbidden",
  "InsufficientUserPermissionForbidden",
  "DepartmentNotAllowedForbidden",
  "PaymentOverdueForbidden",
  "Email is already verified",
  "User is already assigned to an organization",
  "Request failed with status code 401",
  "Request failed with status code 403",
  "Request failed with status code 404"
];

type SentryUserContext = {
  auth0Sub?: string;
  dbUserId?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  organizationId?: string;
  subscriptionType?: string;
  userType?: string;
};

export function getSentryEnvironment(): "development" | "production" | "local" {
  const explicitEnvironment = process.env.REACT_APP_SENTRY_ENVIRONMENT?.trim().toLowerCase();
  if (explicitEnvironment === "production" || explicitEnvironment === "local" || explicitEnvironment === "development") {
    return explicitEnvironment as "development" | "production" | "local";
  }
  return process.env.NODE_ENV === "production" ? "production" : "development";
}

export function getCurrentTraceId(): string | undefined {
  const traceData = Sentry.getTraceData() as Record<string, string | undefined>;
  if (!traceData["sentry-trace"]) return undefined;
  return traceData["sentry-trace"].split("-")[0];
}

export function enrichErrorWithObservability(error: AppError): AppError {
  return {
    ...error,
    traceId: error.traceId ?? getCurrentTraceId(),
  };
}

export function sanitizeErrorResponseBody(value: unknown): Record<string, unknown> | string | undefined {
  if (!value) return undefined;
  if (typeof value === "string") return value.slice(0, 500);
  return value as Record<string, unknown>;
}

export function setSentryUserContext(user: SentryUserContext): void {
  Sentry.setUser({
    id: user.dbUserId,
    email: user.email,
    ...(user.firstName ? { username: `${user.firstName} ${user.lastName}` } : {}),
  });
  
  Sentry.setTags({
    tenant: user.organizationId,
    plan: user.subscriptionType || "noplan",
    user_type: user.userType,
  });
}

export function clearSentryUserContext(): void {
  Sentry.setUser(null);
}

let isInitialized = false;

export function initSentry(): void {
  if (isInitialized) return;

  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn || dsn.trim() === "") return;

  const environment = getSentryEnvironment();
  const isProduction = environment === "production";
  const apiUrl = process.env.REACT_APP_API || DEFAULT_API_URL;
  const traceSampleRate = Number.parseFloat(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE ?? "1") || 1;

  Sentry.init({
    dsn,
    environment,
    release: FRONTEND_RELEASE,
    tracesSampleRate: traceSampleRate,
    replaysSessionSampleRate: isProduction ? 0 : 0,
    replaysOnErrorSampleRate: isProduction ? 0.5 : 0,
    ignoreErrors: IGNORED_ERRORS,
    sendDefaultPii: true, // Send headers, cookies, etc. to help debugging
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracePropagationTargets: ["localhost", /^\/(?!\/)/, apiUrl],
  });

  isInitialized = true;
}
